import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { accountTypeFromMainAccountType } from "~/pages/Compliance/utils/mainAccountTypeUtils";
import {
  accountDomicile,
  accountPositionRows,
  accountPositionsHref,
  accountViewFromNode,
  categoryFromAccountType,
  comparisonStatus,
  joinAddressLines,
  programsFromPositions,
  summaryPositionRows,
  summaryPositionsHref,
  toCell,
  toTagsCell,
  viewableAccountType,
} from "../CompareTable/utils";
import type { CompareCell, InvestorCompareData, InvestorCompareRow } from "./types";
import type {
  AccountPositionsModalData,
  AccountPositionsSource,
  InvestmentAccountNode,
} from "../CompareTable/utils";
import type { ProfileInvestmentAccountDetailsQuery } from "~/graphql/supergraphTypes";

export type InvestmentAccountRow =
  ProfileInvestmentAccountDetailsQuery["profileInvestmentAccountDetails"][number];

type ComparableAccount = {
  clientName: string | null;
  /** Broad Individual / Joint / Entity category, shown in the "Account type" row. */
  accountTypeValue: string | null;
  /** The same category as an enum, used to pick which rows the comparison shows. */
  category: ComplianceObjectType | null;
  /** Fine-grained type such as "Corporation", shown in the "Entity type" row. */
  entityTypeValue: string | null;
  name: string | null;
  taxId: string | null;
  domicileAddress: string | null;
  domicileCountry: string | null;
  positionsCount: number;
  programs: string[];
  /** Data for the positions modal opened from this column's count, when it has positions. */
  accountPositions: AccountPositionsModalData | null;
};

const EXISTING_POSITIONS_LABEL = "Existing positions";

const toAccountPositions = (
  source: AccountPositionsSource,
  accountName: string | null,
): AccountPositionsModalData | null => {
  const rows = accountPositionRows(source);
  return rows.length > 0
    ? { accountName: accountName ?? "", rows, viewAllHref: accountPositionsHref(source) }
    : null;
};

// Existing accounts arrive with main's preformatted `positionsSummary`, so their modal reads the
// summary directly rather than the fuller position graph the current account column uses.
const toSummaryAccountPositions = (
  summaries: InvestmentAccountRow["positionsSummary"],
  accountName: string | null,
  arenaId: number | undefined,
): AccountPositionsModalData | null => {
  const rows = summaryPositionRows(summaries);
  return rows.length > 0
    ? {
        accountName: accountName ?? "",
        rows,
        viewAllHref: summaryPositionsHref(summaries, arenaId),
      }
    : null;
};

type RowSpec = {
  label: string;
  cellFor: (account: ComparableAccount) => CompareCell;
  /** Omitted on rows where a difference is expected rather than notable (counts, program tags). */
  comparableValue?: (account: ComparableAccount) => string | null;
};

const ACCOUNT_CATEGORY_LABEL: Record<ComplianceObjectType, string> = {
  [ComplianceObjectType.Individual]: "Individual",
  [ComplianceObjectType.Joint]: "Joint",
  [ComplianceObjectType.Institution]: "Entity",
};

// The backend derives entity_type as the broad category and leaves account_type as the fine-grained
// value, so both sides derive the category from account_type the same way.
const categoryFromMainAccountType = (
  accountType: string | null | undefined,
): ComplianceObjectType | null => {
  if (!accountType) return null;
  // A present-but-unmatched account type falls back to Entity rather than the "no type" null.
  return (
    categoryFromAccountType(accountTypeFromMainAccountType(accountType)) ??
    ComplianceObjectType.Institution
  );
};

const fromExistingAccount = (
  account: InvestmentAccountRow,
  arenaId: number | undefined,
): ComparableAccount => {
  const domicile = accountDomicile(account.account);
  return {
    clientName: account.clientName,
    accountTypeValue: account.entityType,
    category: categoryFromMainAccountType(account.accountType),
    entityTypeValue: viewableAccountType(
      accountTypeFromMainAccountType(account.accountType) ?? account.accountType,
    ),
    name: account.name,
    taxId: account.taxId,
    domicileAddress: joinAddressLines(domicile.lines),
    domicileCountry: domicile.country,
    positionsCount: account.positionsSummary.length,
    programs: programsFromPositions(account.positionsSummary),
    accountPositions: toSummaryAccountPositions(
      account.positionsSummary,
      account.name,
      arenaId,
    ),
  };
};

const fromCurrentAccount = (node: InvestmentAccountNode): ComparableAccount => {
  const view = accountViewFromNode(node);
  return {
    clientName: view.clientName,
    accountTypeValue: view.category ? ACCOUNT_CATEGORY_LABEL[view.category] : null,
    category: view.category,
    entityTypeValue: view.accountType,
    name: view.name,
    taxId: view.taxId,
    domicileAddress: joinAddressLines(view.addressLines),
    domicileCountry: view.domicileCountry,
    positionsCount: view.positionCount,
    programs: view.programs,
    accountPositions: toAccountPositions(node, view.name),
  };
};

const IDENTITY_ROW_SPECS: RowSpec[] = [
  {
    label: "Client",
    cellFor: (account) => toCell(account.clientName),
    comparableValue: (account) => account.clientName,
  },
  {
    label: "Account type",
    cellFor: (account) => toCell(account.accountTypeValue),
    comparableValue: (account) => account.accountTypeValue,
  },
  {
    label: "Entity type",
    cellFor: (account) => toCell(account.entityTypeValue),
    comparableValue: (account) => account.entityTypeValue,
  },
  {
    label: "Name",
    cellFor: (account) => toCell(account.name),
    comparableValue: (account) => account.name,
  },
];

const TAX_ID_ROW_SPEC: RowSpec = {
  label: "ID number",
  cellFor: (account) => toCell(account.taxId),
  comparableValue: (account) => account.taxId,
};

const DOMICILE_ADDRESS_ROW_SPEC: RowSpec = {
  label: "Domicile address",
  cellFor: (account) => toCell(account.domicileAddress),
  comparableValue: (account) => account.domicileAddress,
};

const DOMICILE_COUNTRY_ROW_SPEC: RowSpec = {
  label: "Domicile country",
  cellFor: (account) => toCell(account.domicileCountry),
  comparableValue: (account) => account.domicileCountry,
};

const ACTIVITY_ROW_SPECS: RowSpec[] = [
  {
    label: EXISTING_POSITIONS_LABEL,
    cellFor: (account) =>
      account.accountPositions
        ? {
            value: String(account.positionsCount),
            isLink: true,
            accountPositions: account.accountPositions,
          }
        : toCell(String(account.positionsCount)),
  },
  {
    label: "Compliance programs",
    cellFor: (account) => toTagsCell(account.programs),
  },
];

// Joint ownership has no tax id of its own and only compares domicile at the country level.
const rowSpecsFor = (isJoint: boolean): RowSpec[] => [
  ...IDENTITY_ROW_SPECS,
  ...(isJoint ? [DOMICILE_COUNTRY_ROW_SPEC] : [TAX_ID_ROW_SPEC, DOMICILE_ADDRESS_ROW_SPEC]),
  ...ACTIVITY_ROW_SPECS,
];

// Mirrors the profile-to-account comparison's layout rule; without a current position, the
// existing accounts being compared are the only signal left to decide it.
const isJointComparison = (
  currentAccount: ComparableAccount | null,
  existingAccounts: ComparableAccount[],
): boolean => {
  const accounts = currentAccount ? [currentAccount] : existingAccounts;
  return (
    accounts.length > 0 &&
    accounts.every((account) => account.category === ComplianceObjectType.Joint)
  );
};

const buildRow = (
  spec: RowSpec,
  currentAccount: ComparableAccount | null,
  existingAccounts: ComparableAccount[],
): InvestorCompareRow => {
  const currentValue =
    currentAccount && spec.comparableValue ? spec.comparableValue(currentAccount) : null;

  return {
    label: spec.label,
    currentPosition: currentAccount ? spec.cellFor(currentAccount) : {},
    existingAccounts: existingAccounts.map((account): CompareCell => {
      const cell = spec.cellFor(account);
      const status = comparisonStatus(spec.comparableValue?.(account), currentValue);
      return status ? { ...cell, status } : cell;
    }),
  };
};

// Read by both the snapshot tab's warning icon and the in-view banner, so the two can't disagree.
export const hasAccountsMismatch = (compareData: InvestorCompareData | null): boolean =>
  compareData?.rows.some((row) =>
    row.existingAccounts.some((cell) => cell.status === "mismatch"),
  ) ?? false;

export const buildCompareData = (
  accounts: InvestmentAccountRow[],
  currentAccountNode: InvestmentAccountNode | null,
  currentInvestmentAccountId: number | null,
  arenaId: number | undefined,
): InvestorCompareData | null => {
  const currentAccount = currentAccountNode ? fromCurrentAccount(currentAccountNode) : null;
  const existingAccounts = accounts
    .filter((account) => account.investmentAccountId !== currentInvestmentAccountId)
    .map((account) => fromExistingAccount(account, arenaId));

  if (!currentAccount && existingAccounts.length === 0) return null;

  return {
    currentPositionSubtitle: "Current position",
    existingAccountsHeader: "Existing investment accounts for selected profile",
    existingAccountCount: existingAccounts.length,
    rows: rowSpecsFor(isJointComparison(currentAccount, existingAccounts)).map((spec) =>
      buildRow(spec, currentAccount, existingAccounts),
    ),
  };
};
